import React from "react";
import { Typography } from "@mui/material";
import { COLORS } from "../../common/colors";
import type { DailyStreamDay } from "src/providers/compliance/complianceTypes";
import { DEFAULT_TD_THRESHOLD_HOURS } from "src/providers/compliance/complianceFilters";
import { CHART } from "./complianceColors";
import { formatDay } from "./dayTimeline";
import { DailyBarChart } from "./DailyBarChart";

interface DailyStreamChartProps {
  days: DailyStreamDay[];
  dates: string[];
  mode: "presence" | "bar";
  threshold?: number;
  todayDate?: string;
}

const SMALL_TEXT_SX = {
  fontFamily: "Work Sans, sans-serif",
  color: COLORS.GREY_900
};

function CoverageSummary({
  covered,
  total
}: {
  covered: number;
  total: number;
}): JSX.Element {
  return (
    <Typography sx={{ ...SMALL_TEXT_SX, fontSize: 12, mt: 0.5 }}>
      {covered}d / {total}d
    </Typography>
  );
}

function PresenceChart({
  days,
  dates,
  todayDate
}: {
  days: DailyStreamDay[];
  dates: string[];
  todayDate?: string;
}): JSX.Element {
  const daysByDate = new Map(days.map((d) => [d.date, d]));
  const bars = dates
    .map((date) => daysByDate.get(date))
    .filter((d): d is DailyStreamDay => d !== undefined && d.present)
    .map((d) => {
      const isToday = todayDate && d.date === todayDate;
      const label = formatDay(d.date);
      return {
        date: d.date,
        segments: [
          { value: 1, color: CHART.DATA_FILL, stroke: CHART.DATA_STROKE }
        ],
        tooltip: isToday ? `${label}: in progress` : `${label}: data received`
      };
    });

  return (
    <>
      <DailyBarChart
        dates={dates}
        bars={bars}
        yMax={1}
        yTicks={[0, 1]}
        formatTick={(t) => (t === 1 ? "Y" : "N")}
        yLabel="data received"
        height={32}
        todayDate={todayDate}
      />
      {/* One bar per present day in the window, so the count always matches the chart. */}
      <CoverageSummary covered={bars.length} total={dates.length} />
    </>
  );
}

// Grows past 12h so a higher hours line or a long day isn't drawn at the top edge.
export function hoursAxisMax(values: number[], threshold: number): number {
  const max = Math.max(12, threshold, ...values);
  return Math.ceil(max / 4) * 4;
}

function BarChart({
  days,
  dates,
  threshold,
  todayDate
}: {
  days: DailyStreamDay[];
  dates: string[];
  threshold: number;
  todayDate?: string;
}): JSX.Element {
  const daysByDate = new Map(days.map((d) => [d.date, d]));
  // Any data that day gets a bar: a few seconds of MM4PD rounds to 0.00h but still arrived.
  const present = dates
    .map((date) => daysByDate.get(date))
    .filter((d): d is DailyStreamDay => d !== undefined && d.present);
  const bars = present.map((d) => {
    const isToday = todayDate && d.date === todayDate;
    const label = formatDay(d.date);
    const hours = d.value ?? 0;
    const hoursText =
      d.value === null
        ? "data received"
        : `${hours < 0.1 ? "<0.1" : hours.toFixed(1)}h`;
    // Today's hours are still coming in, so it isn't judged against the line yet.
    const belowLine = !isToday && hours < threshold;

    return {
      date: d.date,
      showAtZero: true,
      segments: [
        {
          value: hours,
          ...(belowLine
            ? { color: CHART.DATA_LOW_FILL, stroke: CHART.DATA_LOW_STROKE }
            : { color: CHART.DATA_FILL, stroke: CHART.DATA_STROKE })
        }
      ],
      tooltip: isToday
        ? `${label}: in progress · ${hoursText}`
        : `${label}: ${hoursText}`
    };
  });

  const yMax = hoursAxisMax(
    present.map((d) => d.value ?? 0),
    threshold
  );
  const yTicks: number[] = [];
  for (let i = 0; i <= yMax; i += 4) yTicks.push(i);

  return (
    <>
      <DailyBarChart
        dates={dates}
        bars={bars}
        yMax={yMax}
        yTicks={yTicks}
        formatTick={(t) => `${t}h`}
        height={80}
        threshold={{ value: threshold, label: `${threshold}h` }}
        todayDate={todayDate}
      />
      <CoverageSummary covered={bars.length} total={dates.length} />
    </>
  );
}

export function DailyStreamChart({
  days,
  dates,
  mode,
  threshold = DEFAULT_TD_THRESHOLD_HOURS,
  todayDate
}: DailyStreamChartProps): JSX.Element {
  if (mode === "bar") {
    return (
      <BarChart
        days={days}
        dates={dates}
        threshold={threshold}
        todayDate={todayDate}
      />
    );
  }
  return <PresenceChart days={days} dates={dates} todayDate={todayDate} />;
}
